import React, { lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import { InputNumber } from "primereact/inputnumber";
import { ToggleButton } from "primereact/togglebutton";
import { TOGGLE_PT } from "../Shared/toggleButtonPt.ts";
import { SettingsContext } from "../MathFactsGenerator/Settings/SettingsContext.tsx";
import { useCanPreviewPdf } from "../Shared/Pdf/useCanPreviewPdf.ts";
import { buildPrintableSheets } from "./deck.ts";
import { CARDS_PER_SHEET } from "./PdfGenerator/cardLayout.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { format } from "../Shared/i18n/format.ts";

// The only route into @react-pdf/renderer from these pages; see the preview.
const PrintableCardsPreview = lazy(() => import("./PdfGenerator/PrintableCardsPreview.tsx"));

/** A cap, so a mistyped page count cannot ask for hundreds of sheets of cards. */
export const MAX_SHEETS = 20;

/**
 * The printable half of the feature: pick how many sheets, whether to print
 * answer backs, and preview or download the PDF.
 *
 * The sheets are built into state on the same terms as the on-screen deck -
 * mount, a settings change, or an explicit reshuffle - because the generator is
 * non-deterministic and the preview and the download link must be handed the
 * same cards. Building them here rather than inside the preview is what lets
 * the whole PDF layer stay behind the lazy import.
 */
export function PrintPanel({ operationLabel }: { operationLabel: string }) {
    const { settings, setSettings } = useContext(SettingsContext);
    const canPreview = useCanPreviewPdf();
    const [sheets, setSheets] = useState(() => buildPrintableSheets(settings));
    const t = useTranslation();

    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false;
            return;
        }
        setSheets(buildPrintableSheets(settings));
    }, [settings]);

    function handleSheetsChange(e) {
        let value = parseInt(e.value);
        if (value > MAX_SHEETS) {
            value = MAX_SHEETS;
        }
        setSettings({ ...settings, NumberOfPages: isNaN(value) ? 1 : value });
    }

    function handleAnswerBacksToggle(value: boolean) {
        setSettings({ ...settings, AnswerPages: value });
    }

    const cardCount = sheets.reduce((total, sheet) => total + sheet.length, 0);
    const requested = CARDS_PER_SHEET * Math.max(1, settings.NumberOfPages);
    // Every card is different, so a narrow range runs out of facts before it
    // fills the sheets asked for. Say so rather than quietly printing fewer.
    const cappedByPool = cardCount < requested;

    return (
        <section className="printableCards">
            <h2>{t('flashCards.printTheseCards')}</h2>
            <p className="printableCardsIntro">
                {format(t('flashCards.printIntroPrefix'), { count: CARDS_PER_SHEET })}
                {settings.AnswerPages
                    ? t('flashCards.printIntroDoubleSided')
                    : t('flashCards.printIntroSingleSided')}
            </p>

            <div className="printableCardsSettings">
                <div className="printableCardsSetting">
                    <label htmlFor="cardSheets">{t('flashCards.sheetsOfCardsLabel')}</label>
                    <InputNumber
                        inputId="cardSheets"
                        className="pagesInput"
                        value={settings.NumberOfPages}
                        onChange={handleSheetsChange}
                        showButtons
                        pt={{
                            incrementButton: { "aria-label": t('flashCards.addSheetAria') },
                            decrementButton: { "aria-label": t('flashCards.removeSheetAria') },
                        }}
                    />
                </div>
                <div className="printableCardsSetting">
                    <label htmlFor="answerBacks">{t('flashCards.answersOnBackLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')}
                        inputId="answerBacks"
                        className="answerPagesButton" pt={TOGGLE_PT}
                        checked={settings.AnswerPages}
                        onChange={(e) => handleAnswerBacksToggle(e.value)}
                    />
                </div>
            </div>

            <p className="printableCardsCount" aria-live="polite">
                {cardCount} {operationLabel} {t('flashCards.cards')}
                {settings.AnswerPages ? t('flashCards.withAnswerBacks') : ""}
                {sheets.length > 0 &&
                    format(t('flashCards.onSheetsSuffix'), {
                        n: sheets.length,
                        sheetWord: sheets.length === 1 ? t('flashCards.sheet') : t('flashCards.sheets'),
                    })}
            </p>

            {cappedByPool && cardCount > 0 && (
                <p className="printableCardsNote">
                    {format(t('flashCards.cappedByPoolNote'), {
                        count: cardCount,
                        operation: operationLabel,
                        factWord: cardCount === 1 ? t('flashCards.fact') : t('flashCards.facts'),
                    })}
                </p>
            )}

            {cardCount === 0 ? (
                <p className="printableCardsNote">
                    {t('flashCards.chooseOperationNote')}
                </p>
            ) : (
                <Suspense fallback={<div className="pdfLoading">{t('flashCards.loadingPreview')}</div>}>
                    <PrintableCardsPreview
                        sheets={sheets}
                        answerBacks={settings.AnswerPages}
                        canPreview={canPreview}
                        fileName={`${operationLabel}-flash-cards.pdf`}
                        eventParams={{
                            operation: operationLabel,
                            cards: cardCount,
                            answer_backs: settings.AnswerPages,
                        }}
                    />
                </Suspense>
            )}
        </section>
    );
}
